// frontend/src/components/Leave/CalendarGrid.js
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, X, Users, Building } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { mapToGenericLeaveType, getGenericLeaveTypeColor } from '../../utils/leaveTypeMapping';
import MonthNavigator from '../UI/MonthNavigator';
import LoadingSpinner from '../UI/LoadingSpinner';
import PillToggle from '../UI/PillToggle';
import { FilterButton } from '../UI/FilterButton';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { useViewportAnchoredPanel, getViewportPanelStyle } from '../../hooks/useViewportAnchoredPanel';
import Portal from '../UI/Portal';
import { SessionPeriodChip } from '../UI/SessionPeriodChip';

/**
 * Reusable calendar grid component for displaying leave applications
 * Styled for employee theme (navy blue) following design system
 * 
 * @param {Object} props
 * @param {Array} props.applications - Leave applications to display
 * @param {Array} props.publicHolidays - Public holidays array with {date, name}
 * @param {Date} props.currentDate - Current month/year to display
 * @param {Function} props.onDateClick - Callback when a date is clicked
 * @param {Function} props.onMonthChange - Callback when month navigation changes (direction: -1 or 1)
 * @param {Function} props.onTodayClick - Callback when "Today" button is clicked
 * @param {string} props.scope - Current scope ('department' or 'company')
 * @param {Function} props.onScopeChange - Callback when scope changes
 * @param {boolean} props.showActualLeaveTypes - If true, show actual leave type names; if false, show generic labels
 * @param {string} props.searchTerm - Search term for filtering
 * @param {Function} props.onSearchChange - Callback when search term changes
 * @param {Array} props.selectedStaffIds - Array of selected staff IDs
 * @param {Function} props.onStaffFilterToggle - Callback to toggle staff filter dropdown
 * @param {boolean} props.showStaffFilter - Whether staff filter dropdown is visible
 * @param {Function} props.onStaffFilterClose - Callback to close staff filter dropdown
 * @param {string} props.staffFilterSearch - Search term for staff filter dropdown
 * @param {Function} props.onStaffFilterSearchChange - Callback when staff filter search changes
 * @param {Array} props.availableStaff - Available staff list
 * @param {Array} props.filteredStaffForDropdown - Filtered staff for dropdown
 * @param {Set} props.selectedStaffIdsSet - Set of selected staff IDs for O(1) lookup
 * @param {Function} props.onStaffToggle - Callback when staff is toggled
 * @param {Function} props.onClearFilters - Callback to clear all filters
 * @param {boolean} props.staffListLoading - Whether staff list is loading
 * @param {Error} props.staffListError - Staff list error
 * @param {Function} props.getStaffName - Function to get staff name by ID
 */
const CalendarGrid = ({ 
  applications = [], 
  publicHolidays = [], 
  currentDate = new Date(),
  onDateClick,
  onMonthChange,
  onTodayClick,
  scope = 'department',
  onScopeChange,
  showActualLeaveTypes = false,
  searchTerm = '',
  onSearchChange,
  selectedStaffIds = [],
  onStaffFilterToggle,
  showStaffFilter = false,
  onStaffFilterClose,
  staffFilterSearch = '',
  onStaffFilterSearchChange,
  availableStaff = [],
  filteredStaffForDropdown = [],
  selectedStaffIdsSet = new Set(),
  onStaffToggle,
  onClearFilters,
  staffListLoading = false,
  staffListError = null,
  getStaffName = () => '',
  selectedDate = null,
  userDepartment = ''
}) => {
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getApplicationsForDate = (date) => {
    if (!date) return [];
    
    // Format date to match backend data
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const filtered = applications.filter(app => {
      // Backend already filters by status (approved/auto-approved), so we only need to check date range
      // Parse application dates and compare as date strings
      const startDateStr = app.leave_start_date ? app.leave_start_date.split('T')[0] : '';
      const endDateStr = app.leave_end_date ? app.leave_end_date.split('T')[0] : '';
      
      if (!startDateStr || !endDateStr) {
        return false; // Skip applications with missing dates
      }
      
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
    
    return filtered;
  };

  const getDateKey = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTimeBlockForDate = (app, date) => {
    const dateStr = getDateKey(date);
    const startDateStr = app.leave_start_date ? app.leave_start_date.split('T')[0] : '';
    const endDateStr = app.leave_end_date ? app.leave_end_date.split('T')[0] : '';
    const displayChips = app.leave_display_period_chips || {};

    if (dateStr === startDateStr && displayChips.start) return displayChips.start;
    if (dateStr === endDateStr && displayChips.end) return displayChips.end;

    const isSingleDay = startDateStr && startDateStr === endDateStr;
    const isHalfDay = (parseFloat(app.leave_total_days) === 0.5 || parseFloat(app.leave_days) === 0.5);
    const durationIsFractional = Number.isFinite(parseFloat(app.leave_total_days))
      && parseFloat(app.leave_total_days) % 1 !== 0;
    if (isSingleDay && isHalfDay) return app.leave_start_time_period || null;
    if (dateStr === startDateStr && app.leave_start_time_period === 'PM' && durationIsFractional) return 'PM';
    if (dateStr === endDateStr && app.leave_end_time_period === 'AM' && durationIsFractional) return 'AM';
    return null;
  };

  const isPublicHoliday = (date) => {
    if (!date) return false;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return publicHolidays.some(h => h.date === dateStr);
  };

  const getPublicHolidayName = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    const hit = publicHolidays.find(h => h.date === dateStr);
    return hit?.name || 'Public Holiday';
  };

  const getDateColor = (date) => {
    if (isPublicHoliday(date)) return 'bg-red-200 hover:bg-red-300 border-red-400';
    return 'bg-white hover:bg-gray-50';
  };

  const getDotColor = (leaveType) => {
    const map = {
      'Out of Office': 'bg-blue-500',
      'Unavailable': 'bg-amber-500',
      'Extended Leave': 'bg-rose-500',
      'Work From Home': 'bg-green-500',
      'External Activity': 'bg-teal-500',
      'On Course': 'bg-violet-500',
      'NS Leave': 'bg-lime-500',
    };
    return map[leaveType] || 'bg-gray-400';
  };

  const handleDateClick = (date) => {
    if (date && onDateClick) {
      onDateClick(date);
    }
  };

  const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const handleMonthNavigate = (newYm) => {
    if (!onMonthChange) return;
    const [ny, nm] = newYm.split('-').map(Number);
    const cy = currentDate.getFullYear();
    const cm = currentDate.getMonth() + 1;
    const diff = (ny - cy) * 12 + (nm - cm);
    onMonthChange(diff);
  };

  const handleToday = () => {
    if (onTodayClick) {
      onTodayClick();
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNamesShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dayNamesFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                         currentDate.getFullYear() === today.getFullYear();

  const selectedDateApps = selectedDate ? getApplicationsForDate(selectedDate) : [];
  const selectedDateIsHoliday = selectedDate ? isPublicHoliday(selectedDate) : false;
  const selectedDateHolidayName = selectedDateIsHoliday ? getPublicHolidayName(selectedDate) : null;

  // Department grouping for mobile panel (mirrors GroupedDateLeaveModal logic)
  const groupedDepartments = useMemo(() => {
    if (!selectedDateApps.length) return [];
    const groups = {};
    selectedDateApps.forEach(app => {
      const dept = app.department_name || app.department || 'Other';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(app);
    });
    return Object.keys(groups)
      .sort((a, b) => {
        if (a === userDepartment) return -1;
        if (b === userDepartment) return 1;
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return a.localeCompare(b);
      })
      .map(dept => ({ department: dept, apps: groups[dept] }));
  }, [selectedDateApps, userDepartment]);

  const isSingleDepartment = groupedDepartments.length <= 1;

  const [collapsedDepts, setCollapsedDepts] = useState(new Set());

  // Smart auto-collapse when selected date or grouping changes
  const selectedDateKey = selectedDate ? selectedDate.toDateString() : null;
  useMemo(() => {
    if (isSingleDepartment || groupedDepartments.length <= 2) {
      setCollapsedDepts(new Set());
    } else {
      setCollapsedDepts(new Set(
        groupedDepartments
          .map(g => g.department)
          .filter(d => d !== userDepartment)
      ));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey, groupedDepartments.length]);

  const toggleDept = useCallback((dept) => {
    setCollapsedDepts(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  }, []);

  const staffFilterAnchorRef = useRef(null);
  const { panelRect: staffFilterPanelRect } = useViewportAnchoredPanel({
    open: showStaffFilter,
    anchorRef: staffFilterAnchorRef,
    maxWidth: 320,
    minWidth: 200,
  });

  return (
    <div className="space-y-6">
      {/* Calendar Controls */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <MonthNavigator
            month={currentMonthKey}
            onChange={handleMonthNavigate}
            theme="employee"
            spreadOnMobile
            className="w-full lg:w-auto"
          />

          <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap lg:flex-1 lg:justify-end">
            {/* Search Bar */}
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={(val) => onSearchChange && onSearchChange(val)}
              placeholder="Search by name or staff ID..."
              theme="employee"
              className="flex-1 lg:flex-initial lg:min-w-[200px] lg:max-w-[300px]"
            />

            {/* Staff Filter Icon Button — anchor ref used for viewport-safe panel placement */}
            <div className="relative shrink-0" ref={staffFilterAnchorRef}>
              <FilterButton
                theme="employee"
                isOpen={showStaffFilter}
                activeCount={selectedStaffIds.length}
                onClick={onStaffFilterToggle}
                title={selectedStaffIds.length > 0 ? `${selectedStaffIds.length} staff selected` : 'Filter staff'}
              />

              {/* Staff Filter Dropdown — portaled to body so PullToRefresh /
                  overflow-x-hidden ancestors cannot clip the fixed panel. */}
              {showStaffFilter && (
                <Portal>
                  <div
                    className="fixed inset-0 z-[60]"
                    onClick={onStaffFilterClose}
                    aria-hidden="true"
                  />
                  <div
                    className="fixed z-[70] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                    role="dialog"
                    aria-label="Select Staff"
                    style={getViewportPanelStyle(staffFilterPanelRect)}
                  >
                    <div className="flex-shrink-0 border-b bg-gray-50 p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 min-w-0 truncate">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">Select Staff</span>
                        </h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {filteredStaffForDropdown.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const allFilteredIds = filteredStaffForDropdown.map(s => s.staff_id);
                                const allSelected = allFilteredIds.every(id => selectedStaffIdsSet.has(id));
                                // Toggle each filtered staff member
                                allFilteredIds.forEach(id => {
                                  if (allSelected || !selectedStaffIdsSet.has(id)) {
                                    onStaffToggle && onStaffToggle(id);
                                  }
                                });
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {filteredStaffForDropdown.every(s => selectedStaffIdsSet.has(s.staff_id))
                                ? 'Deselect All'
                                : 'Select All'}
                            </button>
                          )}
                          {selectedStaffIds.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onClearFilters && onClearFilters();
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      </div>
                      <input
                        type="text"
                        placeholder="Search staff..."
                        value={staffFilterSearch}
                        onChange={(e) => onStaffFilterSearchChange && onStaffFilterSearchChange(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                      {staffListLoading ? (
                        <div className="p-4 text-center">
                          <LoadingSpinner size="sm" />
                          <p className="text-sm text-gray-500 mt-2">Loading staff list...</p>
                        </div>
                      ) : staffListError ? (
                        <div className="p-4 text-center text-sm text-red-600">
                          <p>Failed to load staff list</p>
                          <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                          >
                            Retry
                          </button>
                        </div>
                      ) : filteredStaffForDropdown.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          {staffFilterSearch ? 'No staff found' : 'No staff available'}
                        </div>
                      ) : (
                        <div className="p-2">
                          {filteredStaffForDropdown.map((staff) => {
                            const isSelected = selectedStaffIdsSet.has(staff.staff_id);
                            const staffName = `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.staff_id;
                            return (
                              <label
                                key={staff.staff_id}
                                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => onStaffToggle && onStaffToggle(staff.staff_id)}
                                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {staffName}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {staff.staff_id}
                                    {staff.department?.department_name && ` • ${staff.department.department_name}`}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {selectedStaffIds.length > 0 && (
                      <div className="flex-shrink-0 border-t bg-gray-50 p-3">
                        <div className="flex flex-wrap gap-2">
                          {selectedStaffIds.map((staffId) => (
                            <span
                              key={staffId}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                            >
                              {getStaffName(staffId)}
                              <button
                                type="button"
                                onClick={() => onStaffToggle && onStaffToggle(staffId)}
                                className="hover:text-blue-900"
                                aria-label={`Remove ${getStaffName(staffId)}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Portal>
              )}
            </div>

            {/* Scope Segmented Control */}
            {onScopeChange && (
              <PillToggle
                value={scope}
                onChange={(val) => onScopeChange(val)}
                options={[
                  { id: 'department', label: 'Department view', icon: Users },
                  { id: 'company', label: 'Company view', icon: Building },
                ]}
                variant="icon"
                theme="employee"
                ariaLabel="Calendar scope"
              />
            )}
            {!isCurrentMonth && (
              <button
                onClick={handleToday}
                className="btn btn-primary"
              >
                Today
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar Grid — no overflow-hidden on the shell; corner rounding is applied
          to header + bottom-row cells so today/selection rings are not clipped. */}
      <div className="bg-white rounded-2xl shadow-sm border" role="grid" aria-label="Leave calendar">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 rounded-t-2xl overflow-hidden">
          {dayNames.map((day, dayIdx) => (
            <div key={day} className="p-2 sm:p-4 text-center text-xs sm:text-sm font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
              <span className="sm:hidden">{dayNamesShort[dayIdx]}</span>
              <span className="hidden sm:inline">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            const dateApplications = date ? getApplicationsForDate(date) : [];
            const isToday = date && date.toDateString() === today.toDateString();
            const isLastColumn = index % 7 === 6;
            const isLastRow = Math.floor(index / 7) === Math.floor((days.length - 1) / 7);
            const isBottomLeft = isLastRow && index % 7 === 0;
            const isBottomRight = isLastRow && isLastColumn;
            const cellSurface = isToday
              ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset z-[1]'
              : date
                ? `cursor-pointer ${getDateColor(date)}`
                : 'bg-gray-25';

            return (
              <div
                key={index}
                className={`min-h-[56px] sm:min-h-[120px] p-1 sm:p-2 ${
                  isLastRow ? '' : 'border-b border-gray-200'
                } ${
                  isLastColumn ? '' : 'border-r'
                } ${cellSurface} ${isBottomLeft ? 'rounded-bl-2xl' : ''} ${isBottomRight ? 'rounded-br-2xl' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                {date && (
                  <>
                    <div className="flex flex-col">
                      <div className={`text-xs sm:text-sm font-medium mb-0.5 sm:mb-1 ${isToday ? 'text-blue-800 font-bold' : 'text-gray-900'}`}>
                        {date.getDate()}
                      </div>
                      {isPublicHoliday(date) && (
                        <div
                          className="hidden sm:block badge badge-error text-xs leading-tight py-1 px-2 whitespace-normal break-words mb-1"
                          title={getPublicHolidayName(date)}
                          aria-label={getPublicHolidayName(date)}
                          style={{fontSize: '10px', lineHeight: '1.2'}}
                        >
                          {getPublicHolidayName(date)}
                        </div>
                      )}
                    </div>
                    {/* Mobile: colored dots */}
                    <div className="flex flex-wrap gap-0.5 items-center sm:hidden mt-0.5">
                      {dateApplications.slice(0, 4).map((app, appIndex) => {
                        const leaveTypeName = app.leave_type_name ||
                                             app.leave_type?.leave_type_name ||
                                             app.leave_type_id?.leave_type_name ||
                                             'Unknown';
                        const displayLeaveType = showActualLeaveTypes 
                          ? leaveTypeName 
                          : mapToGenericLeaveType(leaveTypeName);
                        const dotColor = getDotColor(displayLeaveType);

                        return (
                          <div
                            key={appIndex}
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
                          />
                        );
                      })}
                      {dateApplications.length > 4 && (
                        <span className="text-[8px] text-gray-500 leading-none ml-0.5">
                          +{dateApplications.length - 4}
                        </span>
                      )}
                    </div>
                    {/* Desktop: text badges */}
                    <div className="space-y-1 hidden sm:block">
                      {dateApplications.slice(0, 3).map((app, appIndex) => {
                        const leaveTypeName = app.leave_type_name ||
                                             app.leave_type?.leave_type_name ||
                                             app.leave_type_id?.leave_type_name ||
                                             'Unknown';
                        const displayLeaveType = showActualLeaveTypes 
                          ? leaveTypeName 
                          : mapToGenericLeaveType(leaveTypeName);
                        const badgeClass = getGenericLeaveTypeColor(displayLeaveType);
                        const timeBlock = getTimeBlockForDate(app, date);

                        return (
                          <div
                            key={appIndex}
                            className={`text-xs p-1 rounded truncate border ${badgeClass}`}
                            title={`${app.employee_name} - ${displayLeaveType}${timeBlock ? ` (${timeBlock})` : ''}`}
                          >
                            {app.employee_name}
                          </div>
                        );
                      })}
                      {dateApplications.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{dateApplications.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Day Detail Panel */}
      {selectedDate && (
        <div className="sm:hidden bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {dayNamesFull[selectedDate.getDay()]}, {selectedDate.getDate()} {monthNames[selectedDate.getMonth()]}
            </h3>
            <span className="text-xs text-gray-500">
              {selectedDateApps.length} {selectedDateApps.length === 1 ? 'leave' : 'leaves'}
            </span>
          </div>
          {selectedDateHolidayName && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-100">
              <span className="text-xs font-medium text-red-700">{selectedDateHolidayName}</span>
            </div>
          )}
          {/* Department summary chips (multi-department only) */}
          {!isSingleDepartment && groupedDepartments.length > 0 && (
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-1.5 overflow-x-auto scrollbar-hide scroll-smooth">
              {groupedDepartments.map(({ department, apps }) => {
                const isExpanded = !collapsedDepts.has(department);
                return (
                  <button
                    key={department}
                    onClick={() => toggleDept(department)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap flex-shrink-0 transition-colors ${
                      isExpanded
                        ? 'bg-blue-800 text-white border-blue-800'
                        : 'text-gray-500 border-transparent'
                    }`}
                  >
                    {department}
                    <span className={`inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold ${
                      isExpanded ? 'bg-white text-blue-800' : 'bg-blue-100 text-blue-800'
                    }`}>{apps.length}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
            {selectedDateApps.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                No leave applications
              </div>
            ) : isSingleDepartment ? (
              /* Single department: flat list, no section headers */
              <div className="space-y-2 p-3">
                {selectedDateApps.map((app, idx) => {
                  const leaveTypeName = app.leave_type_name ||
                    app.leave_type?.leave_type_name ||
                    app.leave_type_id?.leave_type_name || 'Unknown';
                  const displayLeaveType = showActualLeaveTypes
                    ? leaveTypeName
                    : mapToGenericLeaveType(leaveTypeName);
                  const badgeClass = getGenericLeaveTypeColor(displayLeaveType);
                  const timeBlock = getTimeBlockForDate(app, selectedDate);
                  return (
                    <div key={idx} className="px-4 py-3 flex items-center justify-between gap-3 border border-gray-200 rounded-xl">
                      <span className="text-sm font-medium text-gray-900 truncate">{app.employee_name}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${badgeClass}`}>
                          {displayLeaveType}
                        </span>
                        <SessionPeriodChip session={timeBlock} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Multi-department: collapsible sections */
              <div className="space-y-3 p-3">
                {groupedDepartments.map(({ department, apps }) => {
                  const isCollapsed = collapsedDepts.has(department);
                  return (
                    <div key={department} className="border border-gray-200 rounded-xl p-3">
                      <button
                        onClick={() => toggleDept(department)}
                        className="flex items-center gap-2 w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        {isCollapsed
                          ? <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          : <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        }
                        <span className="text-xs font-semibold text-gray-700 truncate">{department}</span>
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-gray-200 text-gray-600 text-[10px] font-semibold flex-shrink-0">
                          {apps.length}
                        </span>
                      </button>
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-100">
                          {apps.map((app, idx) => {
                            const leaveTypeName = app.leave_type_name ||
                              app.leave_type?.leave_type_name ||
                              app.leave_type_id?.leave_type_name || 'Unknown';
                            const displayLeaveType = showActualLeaveTypes
                              ? leaveTypeName
                              : mapToGenericLeaveType(leaveTypeName);
                            const badgeClass = getGenericLeaveTypeColor(displayLeaveType);
                            const timeBlock = getTimeBlockForDate(app, selectedDate);
                            return (
                              <div key={idx} className="pl-9 pr-4 py-2.5 flex items-center justify-between gap-3">
                                <span className="text-sm font-medium text-gray-900 truncate">{app.employee_name}</span>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${badgeClass}`}>
                                    {displayLeaveType}
                                  </span>
                                  <SessionPeriodChip session={timeBlock} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarGrid;

